import { Injectable } from '@angular/core';
import { UpsertWorkoutStep } from '@me-fit-mono/typings';
import { TranslateService } from '@ngx-translate/core';
import { ExerciseRendererService } from 'src/app/modules/workout-viewer/exercise-renderer.service';
import { readCssVariable } from 'src/app/utils/read-css-variable';

export interface PrintWorkoutParams {
  name: string;
  description: string;
  steps: UpsertWorkoutStep[];
  createdInMeFitProUrl: string;
  exerciseNames?: Record<string, string>;
}

interface PrintLabels {
  createdInMeFitPro: string;
  exercise: string;
  repetitions: string;
  duration: string;
  sets: string;
  weight: string;
  break: string;
  notes: string;
}

export interface StoredPrintWorkoutParams extends PrintWorkoutParams {
  labels: PrintLabels;
}

interface PrintStoredWorkoutParams {
  printId: string;
}

interface PrintTheme {
  secondary: string;
  fontFamily: string;
}

@Injectable({
  providedIn: 'root',
})
export class WorkoutPrintService {
  private readonly storageKeyPrefix = 'workout-print-';

  constructor(
    private readonly $translate: TranslateService,
    private readonly $exerciseRenderer: ExerciseRendererService
  ) {}

  async print({ name, description, steps, createdInMeFitProUrl, exerciseNames }: PrintWorkoutParams) {
    const printId = crypto.randomUUID();
    const storageKey = this.getStorageKey(printId);

    const printWorkout: StoredPrintWorkoutParams = {
      name,
      description,
      steps,
      createdInMeFitProUrl,
      exerciseNames,
      labels: this.getPrintLabels(),
    };

    window.sessionStorage.setItem(storageKey, JSON.stringify(printWorkout));

    const printWindow = window.open(this.getPrintUrl(printId), '_blank');

    if (!printWindow) {
      window.sessionStorage.removeItem(storageKey);
      return;
    }
  }

  async printStoredWorkout({ printId }: PrintStoredWorkoutParams) {
    const printWorkout = this.getStoredWorkout(printId);

    if (!printWorkout) {
      return;
    }

    window.sessionStorage.removeItem(this.getStorageKey(printId));

    const { name, description, steps, createdInMeFitProUrl, labels, exerciseNames: storedExerciseNames } = printWorkout;
    const resolvedExerciseNames = await Promise.all(
      steps.map(async (step, index) => (
        storedExerciseNames?.[step.exerciseId]
        || await this.$exerciseRenderer.getExerciseName(step.exerciseId)
        || `${labels.exercise} ${index + 1}`
      ))
    );
    const printableSteps = steps.map((step, index) => this.getPrintableStep({
      step,
      exerciseName: resolvedExerciseNames[index],
      labels,
    }));
    const logoUrl = new URL('assets/me-fit-logo-black-background.png', window.location.origin).href;
    const theme = this.getThemeValues();

    window.document.write(this.getPrintableDocument({
      name,
      description,
      createdInMeFitProUrl,
      logoUrl,
      printableSteps,
      labels,
      theme,
    }));
    window.document.close();
    window.focus();
    window.print();
  }

  private getPrintUrl(printId: string): string {
    const printUrl = new URL('/print/workout', window.location.origin);
    printUrl.searchParams.set('printId', printId);

    return printUrl.href;
  }

  private getStorageKey(printId: string): string {
    return `${this.storageKeyPrefix}${printId}`;
  }

  private getPrintLabels(): PrintLabels {
    return {
      createdInMeFitPro: this.$translate.instant('workout.print.created.in.mefit.pro'),
      exercise: this.$translate.instant('exercise.singular'),
      repetitions: this.$translate.instant('repetition.plural'),
      duration: this.$translate.instant('duration'),
      sets: this.$translate.instant('sets.singular'),
      weight: this.$translate.instant('workout.step.weight', { unitOfMeasure: 'kg' }),
      break: this.$translate.instant('workout.step.break'),
      notes: this.$translate.instant('notes'),
    };
  }

  private getStoredWorkout(printId: string): StoredPrintWorkoutParams | null {
    const serializedWorkout = window.sessionStorage.getItem(this.getStorageKey(printId));

    if (!serializedWorkout) {
      return null;
    }

    try {
      const workout: unknown = JSON.parse(serializedWorkout);

      return this.isPrintWorkoutParams(workout) ? workout : null;
    } catch {
      return null;
    }
  }

  private isPrintWorkoutParams(value: unknown): value is StoredPrintWorkoutParams {
    if (!value || typeof value !== 'object') {
      return false;
    }

    const workout = value as Record<string, unknown>;

    return typeof workout['name'] === 'string'
      && typeof workout['description'] === 'string'
      && Array.isArray(workout['steps'])
      && typeof workout['createdInMeFitProUrl'] === 'string'
      && typeof workout['labels'] === 'object'
      && workout['labels'] !== null;
  }

  private getPrintableDocument({
    name,
    description,
    createdInMeFitProUrl,
    logoUrl,
    printableSteps,
    labels,
    theme,
  }: {
    name: string;
    description: string;
    createdInMeFitProUrl: string;
    logoUrl: string;
    printableSteps: string[];
    labels: PrintLabels;
    theme: PrintTheme;
  }): string {
    const printableDescription = description
      ? `<p class="description">${this.escapeHtml(description)}</p>`
      : '';
    const createdInMeFitPro = `<p class="created-in-mefit-pro"><a href="${this.escapeHtml(createdInMeFitProUrl)}">${this.escapeHtml(labels.createdInMeFitPro)}</a></p>`;

    return `<!doctype html>
      <html>
        <head>
          <title>${this.escapeHtml(name)}</title>
          <style>
            @page { margin: 18mm; }
            * { box-sizing: border-box; }
            html { background: #fff; color-scheme: light; }
            body { background: #fff; color: #000; font-family: ${theme.fontFamily}; font-size: 12pt; line-height: 1.4; margin: 0; }
            .ion-padding { padding: var(--ion-padding, 16px); }
            header { border-bottom: 2px solid ${theme.secondary}; padding-bottom: 12px; }
            header img { display: block; height: 52px; margin: 0 auto 14px; object-fit: contain; }
            h1 { font-size: 24pt; margin: 0; }
            .description { color: #000; margin: 8px; max-width: 620px; }
            .created-in-mefit-pro { margin: 12px 0 0; }
            .created-in-mefit-pro a { color: #000; text-decoration-color: ${theme.secondary}; text-decoration-thickness: 2px; }
            .step { border-bottom: 1px solid #000; break-inside: avoid; margin: 26px 0 0; padding-bottom: 24px; }
            .step h2 { font-size: 16pt; margin: 0 0 10px; }
            table { border-collapse: collapse; width: 100%; table-layout:fixed}
            th, td { border: 1px solid #000; padding: 8px 10px; text-align: left; vertical-align: middle }
            th { background: #f0f0f0; font-weight: 700; }
            .sets-column { width: 6%; }
            .repetition-column, .duration-column { width: 33%; }
            .weight-column { width: 15%;   white-space: nowrap; }
            .break-column { width: 12%;   white-space: nowrap; }
            .notes-column { width: 25%; }
            .workout-table.without-weight .notes-column {width: 33%;}
            .workout-table.without-weight .break-column {width: 13%;}
            th { background: #f0f0f0; font-weight: 700; }
            .notes { margin: 12px 0 0; }
            .notes strong { display: block; }
          </style>
        </head>
        <body class="ion-padding">
          <header>
            <img src="${logoUrl}" alt="ME Fit">
            <h1>${this.escapeHtml(name)}</h1>
            ${printableDescription}
            ${createdInMeFitPro}
          </header>
          ${printableSteps.join('')}
        </body>
      </html>`;
  }

  private getThemeValues(): PrintTheme {
    return {
      secondary: readCssVariable({ variableName: '--ion-color-secondary' }).trim(),
      fontFamily: readCssVariable({ variableName: '--ion-font-family' }).trim(),
    };
  }

  private getPrintableStep({ step, exerciseName, labels }: {
    step: UpsertWorkoutStep;
    exerciseName: string;
    labels: PrintLabels;
  }): string {
    const typeLabel = step.type === 'repetitions'
      ? labels.repetitions
      : labels.duration;
    const value = step.type === 'interval' ? this.getDurationLabel(step.value) : step.value;
    const hasWeight = step.weightValue != null;
    const breakLabel = step.break ? `${step.break}s` : '';


  const prescriptionParts = [
    `${step.sets} Set`,
    `${value} ${typeLabel}`,
     ];  

     if (hasWeight) {
      prescriptionParts.push(`${step.weightValue} kg`);
    }

    if (step.break) {
      prescriptionParts.push(breakLabel);
    }

    const prescription = prescriptionParts.join(' / ');



const rows = Array.from(
  { length: step.sets },
  (_, index) => `
  <tr>
    <td class="sets-column">${index + 1}</td>
    <td class="repetition-column"></td>
    ${hasWeight ? '<td class="weight-column"></td>' : ''}
    <td class="break-column"></td>
    <td class="notes-column"></td>
  </tr>
`
).join('');


    const notes = step.notes
      ? `<p class="notes"><strong>${labels.notes}</strong>${this.escapeHtml(step.notes)}</p>`
      : '';

    return `<section class="step">
      <h2>${this.escapeHtml(exerciseName)}</h2>
            ${notes}
              <p class="prescription">
              ${this.escapeHtml(prescription)}
             </p>
           <table class="workout-table ${hasWeight ? 'has-weight' : 'without-weight'}">
           <thead>
          <tr>
             <th class="sets-column">${this.escapeHtml(labels.sets)}</th>
             <th class = "repetition-column">${this.escapeHtml(typeLabel)}</th>
              ${hasWeight ? `<th class="weight-column">${this.escapeHtml(labels.weight)}</th>` : ''}           
              <th class = "break-column">${labels.break}</th>
              <th class="notes-column">${this.escapeHtml(labels.notes)}</th>
          </tr>
        </thead>
        <tbody>
        ${rows}
        </tbody>
      </table>
    </section>`;
  }

  private getDurationLabel(seconds: number): string {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;

    return minutes > 0 ? `${minutes}m ${remainingSeconds}s` : `${remainingSeconds}s`;
  }

  private escapeHtml(value: string): string {
    return value.replace(/[&<>"']/g, character => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;'
    }[character] ?? character));
  }
}